<script lang="ts">
  import { onMount } from 'svelte';
  import type { DeviceGroupSummary, DevicePoolSummary, DeviceStatus, DeviceSummary, TenantSummary } from '@ucc/shared';
  import { ApiError, fetchDevicePools, fetchDevices, fetchGroups, fetchTenants } from '$lib/api';
  import { deviceHref } from '$lib/nav';
  import { session } from '$lib/session.svelte';
  import Badge from '$lib/components/Badge.svelte';
  import Card from '$lib/components/Card.svelte';

  let devices = $state<DeviceSummary[]>([]);
  let tenants = $state<TenantSummary[]>([]);
  let groups = $state<DeviceGroupSummary[]>([]);
  let devicePools = $state<DevicePoolSummary[]>([]);
  let loading = $state(true);

  const role = $derived(session.user?.role);
  const isStaff = $derived(role === 'admin' || role === 'user');

  let error = $state<string | null>(null);

  let q = $state('');
  let statusFilter = $state<'all' | DeviceStatus>('all');
  let tenantFilter = $state<string>('all');

  const isAdmin = $derived(session.user?.role === 'admin');

  const tenantName = $derived.by(() => {
    const map = new Map(tenants.map((t) => [t.id, t.name]));
    return (id: string | null | undefined) => (id ? map.get(id) ?? id.slice(0, 8) : '—');
  });

  const groupName = $derived.by(() => {
    const map = new Map(groups.map((g) => [g.id, g.name]));
    return (id: string | null | undefined) => (id ? (map.get(id) ?? '—') : '—');
  });

  const devicePoolName = $derived.by(() => {
    const map = new Map(devicePools.map((p) => [p.id, p.name]));
    return (id: string | null | undefined) => (id ? (map.get(id) ?? '—') : '—');
  });

  const filtered = $derived.by(() => {
    const needle = q.trim().toLowerCase();
    return devices.filter((d) => {
      if (statusFilter !== 'all' && d.status !== statusFilter) return false;
      if (tenantFilter !== 'all' && d.tenantId !== tenantFilter) return false;
      if (!needle) return true;
      const hay = [d.name, d.sku ?? '', d.uid ?? '', d.serial ?? '', d.ownershipStatus ?? '', d.firmwareVersion ?? '', tenantName(d.tenantId)]
        .join(' ')
        .toLowerCase();
      return hay.includes(needle);
    });
  });

  const counts = $derived.by(() => {
    const base = { total: devices.length, online: 0, offline: 0, unknown: 0, disabled: 0, logging: 0 };
    for (const d of devices) {
      if (d.status in base) (base as Record<string, number>)[d.status] += 1;
    }
    return base;
  });

  function statusTone(status: DeviceStatus): 'success' | 'neutral' | 'warning' | 'danger' | 'accent' {
    switch (status) {
      case 'online':
        return 'success';
      case 'offline':
        return 'neutral';
      case 'disabled':
        return 'danger';
      case 'logging':
        return 'accent';
      default:
        return 'neutral';
    }
  }

  /** UI label — never surface API status "unknown" as text. */
  function statusLabel(status: DeviceStatus): string {
    if (status === 'unknown') return '—';
    return status.charAt(0).toUpperCase() + status.slice(1);
  }

  function formatSeen(iso: string | null): string {
    if (!iso) return 'Never';
    const t = new Date(iso).getTime();
    if (Number.isNaN(t)) return '—';
    const delta = Date.now() - t;
    if (delta < 60_000) return 'Just now';
    if (delta < 3_600_000) return `${Math.floor(delta / 60_000)}m ago`;
    if (delta < 86_400_000) return `${Math.floor(delta / 3_600_000)}h ago`;
    return new Date(iso).toLocaleString();
  }

  const POLL_MS = 20_000;

  async function load(opts?: { silent?: boolean }) {
    const silent = opts?.silent === true;
    if (!silent) {
      loading = true;
      error = null;
    }
    try {
      const [dRes, tRes, gRes, pRes] = await Promise.all([
        fetchDevices(),
        fetchTenants().catch(() => ({ tenants: [] as TenantSummary[] })),
        isStaff ? Promise.resolve({ groups: [] as DeviceGroupSummary[] }) : fetchGroups().catch(() => ({ groups: [] as DeviceGroupSummary[] })),
        isStaff ? fetchDevicePools().catch(() => ({ pools: [] as DevicePoolSummary[] })) : Promise.resolve({ pools: [] as DevicePoolSummary[] }),
      ]);
      devices = dRes.devices;
      tenants = tRes.tenants;
      groups = gRes.groups;
      devicePools = pRes.pools;
      if (silent) error = null;
    } catch (err) {
      if (!silent) {
        error = err instanceof ApiError ? err.message : 'Failed to load devices';
        devices = [];
      }
    } finally {
      if (!silent) loading = false;
    }
  }

  onMount(() => {
    void load();
    const tick = () => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    };
    const id = setInterval(tick, POLL_MS);
    const onVis = () => {
      if (document.visibilityState === 'visible') void load({ silent: true });
    };
    document.addEventListener('visibilitychange', onVis);
    return () => {
      clearInterval(id);
      document.removeEventListener('visibilitychange', onVis);
    };
  });
</script>

<div class="page">
  <header class="head">
    <div>
      <p class="eyebrow">Fleet</p>
      <h2>Devices</h2>
      <p class="sub">
        {#if isAdmin}
          All tenants you can access. Click a row for detail.
        {:else}
          Devices in your tenant. Click a row for detail.
        {/if}
      </p>
    </div>
    <div class="head-actions">
      {#if isStaff}
        <a class="claim-link" href="#/devices/register">Register device</a>
      {:else}
        <a class="claim-link" href="#/devices/claim">Claim device</a>
      {/if}
      <button type="button" class="refresh" onclick={() => void load()} disabled={loading}>
        {loading ? 'Loading…' : 'Refresh'}
      </button>
    </div>
  </header>

  <div class="kpis">
    <div class="kpi">
      <span class="kpi-label">Total</span>
      <strong>{counts.total}</strong>
    </div>
    <div class="kpi online">
      <span class="kpi-label">Online</span>
      <strong>{counts.online}</strong>
    </div>
    <div class="kpi">
      <span class="kpi-label">Offline</span>
      <strong>{counts.offline}</strong>
    </div>
    <div class="kpi logging">
      <span class="kpi-label">Logging</span>
      <strong>{counts.logging}</strong>
    </div>
  </div>

  <Card title="Device list" subtitle={`${filtered.length} shown`} padded={false}>
    <div class="toolbar">
      <label class="field grow">
        <span class="sr-only">Search</span>
        <input type="search" placeholder="Search name, serial, firmware…" bind:value={q} />
      </label>
      <label class="field">
        <span class="sr-only">Status</span>
        <select bind:value={statusFilter}>
          <option value="all">All statuses</option>
          <option value="online">Online</option>
          <option value="offline">Offline</option>
          <option value="disabled">Disabled</option>
          <option value="logging">Logging</option>
        </select>
      </label>
      {#if isAdmin && tenants.length > 1}
        <label class="field">
          <span class="sr-only">Tenant</span>
          <select bind:value={tenantFilter}>
            <option value="all">All tenants</option>
            {#each tenants as t (t.id)}
              <option value={t.id}>{t.name}</option>
            {/each}
          </select>
        </label>
      {/if}
    </div>

    {#if error}
      <p class="banner error" role="alert">{error}</p>
    {:else if loading}
      <p class="banner muted">Loading devices…</p>
    {:else if filtered.length === 0}
      <p class="banner muted">No devices match your filters.</p>
    {:else}
      <div class="table-wrap">
        <table>
          <thead>
            <tr>
              {#if isStaff}<th>UID</th>{:else}<th>Name</th>{/if}
              <th>Status</th>
              {#if isStaff}<th>SKU</th>{/if}
              <th>Ownership</th>
              {#if isStaff}<th>PUK / Client</th>{/if}
              {#if isAdmin}<th>Tenant</th>{/if}
              {#if isStaff}<th>Device Pool</th>{:else}<th>Group</th>{/if}
              <th>Firmware</th>
              <th>Last seen</th>
            </tr>
          </thead>
          <tbody>
            {#each filtered as d (d.id)}
              <tr
                tabindex="0"
                onclick={() => {
                  window.location.hash = deviceHref(d.id);
                }}
                onkeydown={(e) => {
                  if (e.key === 'Enter' || e.key === ' ') {
                    e.preventDefault();
                    window.location.hash = deviceHref(d.id);
                  }
                }}
              >
                <td>
                  <a
                    class="name mono"
                    href={deviceHref(d.id)}
                    onclick={(e) => e.stopPropagation()}
                  >{isStaff ? d.uid : d.name}</a>
                </td>
                <td>
                  {#if d.status === 'unknown'}
                    <span class="muted-cell">—</span>
                  {:else}
                    <Badge tone={statusTone(d.status)}>{statusLabel(d.status)}</Badge>
                  {/if}
                </td>
                {#if isStaff}
                  <td class="mono">{d.sku ?? '—'}</td>
                {/if}
                <td class="mono">{d.ownershipStatus}</td>
                {#if isStaff}
                  <td class="mono">
                    {#if d.ownershipStatus === 'claimed'}
                      <span class="muted-cell">{d.tenantName ?? '—'}</span>
                    {:else if d.puk}
                      <span class="puk-cell">{d.puk}</span>
                    {:else}
                      —
                    {/if}
                  </td>
                {/if}
                {#if isAdmin}
                  <td>{tenantName(d.tenantId)}</td>
                {/if}
                {#if isStaff}
                  <td>{devicePoolName(d.devicePoolId)}</td>
                {:else}
                  <td>{groupName(d.groupId)}</td>
                {/if}
                <td class="mono">{d.firmwareVersion ?? '—'}</td>
                <td>{formatSeen(d.lastSeenAt)}</td>
              </tr>
            {/each}
          </tbody>
        </table>
      </div>
    {/if}
  </Card>
</div>

<style>
  .page {
    display: flex;
    flex-direction: column;
    gap: var(--space-5, 1.25rem);
  }

  .head-actions {
    display: flex;
    align-items: center;
    gap: 0.5rem;
    flex-shrink: 0;
  }
  .claim-link {
    font-size: 0.875rem;
    font-weight: 600;
    text-decoration: none;
    padding: 0.45rem 0.85rem;
    border-radius: 8px;
    border: 1px solid color-mix(in srgb, var(--accent) 35%, var(--border));
    background: color-mix(in srgb, var(--accent) 16%, var(--surface));
    color: var(--text);
  }
  .claim-link:hover {
    background: color-mix(in srgb, var(--accent) 26%, var(--surface));
    border-color: color-mix(in srgb, var(--accent) 50%, var(--border));
    color: var(--text);
  }
  .head {
    display: flex;
    align-items: flex-start;
    justify-content: space-between;
    gap: 1rem;
  }

  .eyebrow {
    margin: 0 0 0.25rem;
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.06em;
    color: var(--text-muted);
  }

  h2 {
    margin: 0;
    font-size: var(--text-xl, 1.35rem);
    font-weight: 650;
  }

  .sub {
    margin: 0.35rem 0 0;
    color: var(--text-muted);
    font-size: var(--text-sm, 0.9rem);
  }

  .refresh {
    border: 1px solid var(--border);
    background: var(--surface);
    color: var(--text);
    border-radius: 8px;
    padding: 0.45rem 0.85rem;
    font: inherit;
    cursor: pointer;
  }

  .refresh:disabled {
    opacity: 0.6;
    cursor: default;
  }

  .kpis {
    display: grid;
    grid-template-columns: repeat(4, minmax(0, 1fr));
    gap: 0.75rem;
  }

  .kpi {
    background: var(--surface);
    border: 1px solid var(--border);
    border-radius: 12px;
    padding: 0.85rem 1rem;
    display: flex;
    flex-direction: column;
    gap: 0.25rem;
  }

  .kpi-label {
    font-size: var(--text-xs, 0.75rem);
    color: var(--text-muted);
  }

  .kpi strong {
    font-size: 1.35rem;
    font-weight: 650;
  }

  .kpi.online strong {
    color: var(--success, #15803d);
  }

  .kpi.logging strong {
    color: var(--accent);
  }

  .toolbar {
    display: flex;
    flex-wrap: wrap;
    gap: 0.65rem;
    padding: 0.85rem 1rem;
    border-bottom: 1px solid var(--border);
  }

  .field {
    display: block;
    min-width: 9rem;
  }

  .field.grow {
    flex: 1 1 14rem;
    min-width: 12rem;
  }

  input,
  select {
    width: 100%;
    border: 1px solid var(--border);
    border-radius: 8px;
    padding: 0.5rem 0.65rem;
    font: inherit;
    background: var(--surface);
    color: inherit;
  }

  .banner {
    margin: 0;
    padding: 1.25rem 1rem;
    font-size: var(--text-sm, 0.9rem);
  }

  .banner.muted {
    color: var(--text-muted);
  }

  .banner.error {
    color: var(--danger, #b91c1c);
  }

  .table-wrap {
    overflow-x: auto;
  }

  table {
    width: 100%;
    border-collapse: collapse;
    font-size: var(--text-sm, 0.9rem);
  }

  th,
  td {
    text-align: left;
    padding: 0.7rem 1rem;
    border-bottom: 1px solid var(--border);
    white-space: nowrap;
  }

  th {
    font-size: var(--text-xs, 0.75rem);
    text-transform: uppercase;
    letter-spacing: 0.04em;
    color: var(--text-muted);
    font-weight: 600;
    background: var(--surface-2, #f9fafb);
  }

  tbody tr {
    cursor: pointer;
  }

  tbody tr:hover {
    background: var(--surface-hover);
  }

  tbody tr:focus-visible {
    outline: 2px solid var(--accent);
    outline-offset: -2px;
  }

  .name {
    color: inherit;
    font-weight: 600;
    text-decoration: none;
  }

  .name:hover {
    color: var(--accent);
    text-decoration: underline;
  }

  .mono {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    border: 0;
  }

  @media (max-width: 720px) {
    .kpis {
      grid-template-columns: repeat(2, minmax(0, 1fr));
    }
  }
  tbody tr {
    background: var(--surface);
    color: var(--text);
  }
  tbody tr:hover td {
    background: var(--surface-hover);
  }
  tbody td {
    color: var(--text);
    background: transparent;
    border-bottom-color: var(--border);
  }
  thead th {
    background: var(--bg-elevated);
    color: var(--text-muted);
  }
  .puk-cell {
    font-family: ui-monospace, SFMono-Regular, Menlo, Consolas, monospace;
    letter-spacing: 0.03em;
  }
  .muted-cell {
    color: var(--text-muted);
  }
</style>
